/**
 * Document-locker hooks (P276.2).
 *
 * Upload is raw fetch + FormData (the useImportDictionary P117
 * precedent -- the api client is JSON-only); everything else rides the
 * api client. Extract-run polling mirrors the P144 batch-runs cadence.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, currentAuthToken, type ApiError } from "shared/api/client";
import type {
  DocumentCommitResult,
  DocumentList,
  DocumentTextRow,
  EngineBatchRun,
  IngestBatch,
} from "shared/api/types";

export const documentKeys = {
  all: ["documents"] as const,
  list: () => [...documentKeys.all, "list"] as const,
  text: (id: string) => [...documentKeys.all, "text", id] as const,
  runs: () => [...documentKeys.all, "extract-runs"] as const,
};

export class DocumentUploadError extends Error {
  constructor(
    public status: number,
    detail: string,
  ) {
    super(detail);
  }
}

async function uploadDocuments(files: File[]): Promise<IngestBatch> {
  const token = currentAuthToken();
  const form = new FormData();
  for (const f of files) form.append("files", f);
  const response = await fetch("/v1/admin/documents/upload", {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      // keep statusText
    }
    throw new DocumentUploadError(response.status, detail);
  }
  return (await response.json()) as IngestBatch;
}

export function useDocuments(
  // A7 -- while an extraction run is in flight, the inventory is
  // changing underneath the reader. Without this the preparer who has
  // just committed watches her own file sit at STATUS "pending" with an
  // em dash for TEXT indefinitely, because only the RUN was polled and
  // the list that shows the outcome was not.
  extracting = false,
): UseQueryResult<DocumentList, ApiError> {
  return useQuery({
    queryKey: documentKeys.list(),
    queryFn: () => api.get<DocumentList>("/v1/admin/documents"),
    // Round 9 B4. The `extracting` flag alone was not enough, and the
    // reason is worth keeping: it comes from useExtractRuns, which only
    // polls once a run is ALREADY active. The run is dispatched as a
    // background task, so the single refetch after a commit sees no run
    // yet, polling never starts, and the preparer watches her file sit
    // at "pending" until she reloads by hand.
    //
    // So the list polls on ITS OWN data as well: while any row is still
    // unextracted there is something to wait for, and when none are it
    // stops by itself. Self-correcting, and it does not depend on
    // catching a background task at the right instant.
    refetchInterval: (query) => {
      const rows = query.state.data?.documents;
      const waiting = rows?.some((d) => d.extraction_status === "pending") ?? false;
      return waiting || extracting ? 2500 : false;
    },
  });
}

export function useUploadDocuments(): UseMutationResult<
  IngestBatch,
  DocumentUploadError,
  File[]
> {
  return useMutation({ mutationFn: uploadDocuments });
}

export function useCommitBatch(): UseMutationResult<
  DocumentCommitResult,
  ApiError,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (batchId: string) =>
      api.post<DocumentCommitResult>(`/v1/admin/documents/batches/${batchId}/commit`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentKeys.list() });
      void qc.invalidateQueries({ queryKey: documentKeys.runs() });
    },
  });
}

export function useDiscardBatch(): UseMutationResult<IngestBatch, ApiError, string> {
  return useMutation({
    mutationFn: (batchId: string) =>
      api.post<IngestBatch>(`/v1/admin/documents/batches/${batchId}/discard`),
  });
}

export function useDocumentText(
  id: string | null,
): UseQueryResult<DocumentTextRow, ApiError> {
  return useQuery({
    queryKey: id ? documentKeys.text(id) : [...documentKeys.all, "text", "_off"],
    queryFn: () => api.get<DocumentTextRow>(`/v1/admin/documents/${id}/text`),
    enabled: !!id,
  });
}

export function useSetManualText(): UseMutationResult<
  unknown,
  ApiError,
  { id: string; text: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }) => api.put(`/v1/admin/documents/${id}/text`, { text }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: documentKeys.list() });
      void qc.invalidateQueries({ queryKey: documentKeys.text(vars.id) });
    },
  });
}

export function useDeleteDocument(): UseMutationResult<unknown, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/admin/documents/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: documentKeys.list() }),
  });
}

export function useExtractRuns(): UseQueryResult<EngineBatchRun[], ApiError> {
  return useQuery({
    queryKey: documentKeys.runs(),
    queryFn: () => api.get<EngineBatchRun[]>("/v1/admin/documents/extract-runs"),
    refetchInterval: (query) => {
      const runs = query.state.data;
      const active = runs?.some((r) => r.status === "queued" || r.status === "running");
      return active ? 2000 : false;
    },
  });
}
